/* FI-2174: midnight fills for the planned/utilization boards' type chips.
 *
 * the api hands these boards a palette of light pastels for vehicle- and
 * driver-type identity (get_vehicle_calendar.py:16, get_driver_calendar.py:15)
 * and the components paint it inline: the filter chips at the top of each board
 * and the per-day pills inside the weekly/monthly calendars. those fills are
 * built for the light themes' dark inherited ink, so midnight could only keep
 * them light and force a dark ink back on top (midnight.css:2968) -- which left
 * the chips rendering identically in horizon and midnight, a light-theme block
 * sitting on the dark canvas.
 *
 * css cannot transform a fill it cannot read, so the remap happens here, where
 * the color is applied. the recipe follows the round-16 chip-family doctrine
 * (keep every hue, lift the ink) rather than the gray flattening that erases a
 * type system:
 *
 *   - hue is kept exactly, so a type keeps its identity across themes
 *   - saturation is boosted 1.6x (capped 72%), because a dark fill needs more
 *     chroma than a pastel to read as the same hue
 *   - lightness is solved, not set: a fixed HSL lightness does NOT equalize
 *     perceived brightness (cyan and green carry far more luminance than blue
 *     at the same L, measured 3.07:1 vs 7:1 against the page ink). so each fill
 *     is binary-searched down to a target WCAG luminance instead.
 *
 * the target is a BAND, not a single value: flattening every chip to one
 * luminance also flattens the palette's own near-duplicate greys into each
 * other (measured min pairwise rgb distance 5.4, against 16 in the light
 * theme). mapping each source luminance into 0.035-0.115 keeps the palette's
 * relative brightness and lands at min distance 14 with every chip at >= 4.88:1
 * against var(--fi-text) -- AA, and parity with the light theme's separation.
 */

var SRC_LO = 0.35          // the pastel palette's luminance floor ...
var SRC_HI = 0.95          // ... and ceiling
var DST_LO = 0.035         // the dark band the fills are mapped into
var DST_HI = 0.115
var SAT_BOOST = 1.6
var SAT_CAP = 72

var _ctx = null
var _cache = Object.create(null)

function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi) }

/* normalize any css color (the palette mixes keywords and hex) to [r,g,b].
   canvas fillStyle does the parsing; it returns '#rrggbb' for opaque colors.
   two different sentinels tell a genuinely-black color apart from a value the
   canvas rejected and left untouched. returns null when the color is invalid. */
function toRgb(color) {
  if (!_ctx) {
    if (typeof document === 'undefined') return null
    var c = document.createElement('canvas')
    _ctx = c.getContext && c.getContext('2d')
    if (!_ctx) return null
  }
  _ctx.fillStyle = '#000000'
  _ctx.fillStyle = color
  var a = _ctx.fillStyle
  _ctx.fillStyle = '#ffffff'
  _ctx.fillStyle = color
  var b = _ctx.fillStyle
  if (a !== b) return null                     // rejected: each sentinel survived
  if (a.charAt(0) !== '#' || a.length !== 7) return null   // rgba()/unsupported
  return [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)]
}

function luminance(rgb) {
  var a = rgb.map(function (v) {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255
  var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn
  var l = (mx + mn) / 2, h = 0, s = 0
  if (d) {
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
    h = mx === r ? ((g - b) / d + (g < b ? 6 : 0)) : mx === g ? ((b - r) / d + 2) : ((r - g) / d + 4)
    h *= 60
  }
  return [h, s * 100, l * 100]
}

function hslToRgb(h, s, l) {
  h /= 360; s /= 100; l /= 100
  if (s === 0) { var v = Math.round(l * 255); return [v, v, v] }
  var q = l < 0.5 ? l * (1 + s) : l + s - l * s
  var p = 2 * l - q
  function f(t) {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  return [Math.round(f(h + 1 / 3) * 255), Math.round(f(h) * 255), Math.round(f(h - 1 / 3) * 255)]
}

/* the light pastel -> its midnight counterpart. returns the input untouched
   when it cannot be parsed, so an unexpected api value degrades to today's
   behaviour instead of losing the fill. */
export function midnightFill(color) {
  if (color == null || color === '') return color
  if (color in _cache) return _cache[color]
  var rgb = toRgb(color)
  if (!rgb) { _cache[color] = color; return color }

  var hsl = rgbToHsl(rgb[0], rgb[1], rgb[2])
  var sat = Math.min(hsl[1] * SAT_BOOST, SAT_CAP)
  var t = clamp((luminance(rgb) - SRC_LO) / (SRC_HI - SRC_LO), 0, 1)
  var target = DST_LO + (DST_HI - DST_LO) * t

  var lo = 0, hi = hsl[2], out = rgb
  for (var i = 0; i < 24; i++) {
    var mid = (lo + hi) / 2
    out = hslToRgb(hsl[0], sat, mid)
    if (luminance(out) > target) hi = mid; else lo = mid
  }
  out = hslToRgb(hsl[0], sat, lo)
  var css = 'rgb(' + out[0] + ', ' + out[1] + ', ' + out[2] + ')'
  _cache[color] = css
  return css
}

/* the mixin the boards use. `midnightActive` also drives the root's
   .fi-plan-board--midnight hook, which is what lets midnight.css lift the ink
   on these fills instead of forcing the light theme's dark ink onto them. */
export default {
  computed: {
    midnightActive() {
      return this.$store.state.profile.site_theme === 'midnight'
    },
  },
  methods: {
    // the filter chips at the top of each board
    typeChipStyle(color, selected) {
      var fill = this.midnightActive ? midnightFill(color) : color
      var style = { background: fill }
      if (selected) style.border = '2px solid red'
      return style
    },
    // the per-day pills inside the weekly/monthly calendars
    // (the former per-component getStyleForVehicleID)
    typePillStyle(color) {
      if (color == null) return ''
      var fill = this.midnightActive ? midnightFill(color) : color
      return 'border-radius: 10px; background-color:' + fill
    },
  },
}
